import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircleIcon, ExclamationCircleIcon, XMarkIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";

export const SaveBar = (props) => {
  const {
    hasChanges = false,
    isSaving = false,
    isError = false,
    isSuccess = false,
    errorMessage = null,
    successMessage = null,
    onSave,
    onBack,
    onDismiss,
    saveLabel = null,
    backLabel = null,
    className = "",
    /** Tailwind z-index class — above PMDialog (2000), below confirm modals (3600). Portaled to body. */
    zIndexClassName = "z-[2200]",
    /** Extra bottom offset classes (e.g. page-specific nav). Stacks on --mobile-nav-height. */
    bottomOffsetClassName = "",
    /** Override --mobile-nav-height locally (px). Prefer global CSS var when possible. */
    mobileNavOffset = null,
    alwaysShow = false, // Show bar even when no changes/status
  } = props;
  const [open, setOpen] = useState(alwaysShow || false);
  const { t } = useTranslation();

  useEffect(() => {
    if (alwaysShow || hasChanges || isSaving || isError || isSuccess || onBack) {
      setOpen(true);
      
      // Auto-hide after 3 seconds if success (and not saving/error/changes/back)
      if (isSuccess && !isSaving && !isError && !hasChanges && !alwaysShow && !onBack) {
        const timer = setTimeout(() => {
          setOpen(false);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [hasChanges, isSaving, isError, isSuccess, alwaysShow, onBack]);

  const handleSave = () => {
    if (onSave && typeof onSave === "function") {
      onSave();
    }
  };

  const handleBack = () => {
    if (onBack && typeof onBack === "function") {
      onBack();
    }
  };

  const handleDismiss = () => {
    setOpen(false);
    if (onDismiss && typeof onDismiss === "function") {
      onDismiss();
    }
  };

  const getContent = () => {
    if (isSaving) {
      return {
        icon: (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
        ),
        message: t("Saving") + "...",
        bgColor: "bg-blue-500",
        textColor: "text-white",
      };
    }
    if (isError) {
      return {
        icon: <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />,
        message: errorMessage || t("Error saving changes"),
        bgColor: "bg-red-500",
        textColor: "text-white",
      };
    }
    if (isSuccess) {
      return {
        icon: <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />,
        message: successMessage || t("Changes saved successfully"),
        bgColor: "bg-green-500",
        textColor: "text-white",
      };
    }
    if (hasChanges) {
      return {
        icon: null,
        message: t("You have unsaved changes"),
        bgColor: "bg-yellow-500",
        textColor: "text-white",
      };
    }
    return null;
  };

  const content = getContent();
  
  // Determine background color - use neutral if no content but showing for back button
  const bgColor = content?.bgColor || (onBack ? "bg-gray-50 border border-gray-200" : "");
  const textColor = content?.textColor || (onBack ? "text-gray-900" : "");
  const message = content?.message || (onBack ? "" : "");
  
  // Show bar if there's content, changes, or if alwaysShow/onBack is set
  if (!content && !hasChanges && !alwaysShow && !onBack) return null;

  const dockStyle =
    mobileNavOffset != null
      ? { "--mobile-nav-height": `${mobileNavOffset}px` }
      : undefined;

  const saveBar = (
    <Transition.Root show={open} as={Fragment}>
      <Transition.Child
        as={Fragment}
        enter="transition ease-out duration-300"
        enterFrom="opacity-0 translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-200"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-2"
      >
        <div
          className={`pointer-events-auto fixed inset-x-0 bottom-[max(var(--mobile-nav-height,0px),var(--vv-keyboard-inset,0px))] pb-[max(0.5rem,env(safe-area-inset-bottom,0px))] pt-2 ${zIndexClassName} ${bottomOffsetClassName} ${className}`}
          style={dockStyle}
        >
          <div className="mx-auto max-w-5xl px-3 sm:px-4 lg:px-6">
            <div className={`rounded-lg ${bgColor} p-3 sm:p-4 shadow-lg`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  {content?.icon && (
                    <div className={`flex-shrink-0 ${textColor}`}>{content.icon}</div>
                  )}
                  {message && (
                    <p className={`text-xs sm:text-sm font-medium ${textColor} break-words`}>
                      {message}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {onBack && (
                    <button
                      onClick={handleBack}
                      className="flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors whitespace-nowrap"
                      type="button"
                    >
                      <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
                      {backLabel || t("Back")}
                    </button>
                  )}
                  {hasChanges && !isSaving && !isError && !isSuccess && onSave && (
                    <button
                      onClick={handleSave}
                      className="flex items-center justify-center rounded-md border border-transparent bg-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-yellow-500 transition-colors whitespace-nowrap"
                      type="button"
                    >
                      {saveLabel || t("Save")}
                    </button>
                  )}
                  {!alwaysShow && !onBack && (
                    <button
                      type="button"
                      onClick={handleDismiss}
                      className={`flex rounded-md p-1.5 sm:p-2 hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-white ${textColor} transition-opacity`}
                      aria-label="Dismiss"
                    >
                      <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition.Child>
    </Transition.Root>
  );

  if (typeof document === "undefined") {
    return saveBar;
  }

  return createPortal(saveBar, document.body);
};
